import { ForestOwnerSandboxBadges } from '@features/Framework/ForestOwnerSandbox/ForestOwnerSandboxBadges'
import { forestOwnerSandboxProfileContext } from '@features/Framework/ForestOwnerSandbox/forestOwnerSandboxProfileContext'
import { useForestOwnerSandboxTabs } from '@features/Framework/ForestOwnerSandbox/useForestOwnerSandboxTabs'
import { EntityProfileLayout } from '@features/Shared/EntityProfile/EntityProfileLayout'
import type { EntityProfileHeaderProps } from '@features/Shared/EntityProfile/types'
import { getCooperativeMembershipQuery, getForestOwnerQuery } from '@services/queryOptions'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { formatFullName } from '@utils/helper-functions'
import { useTenant } from '@/contexts/TenantContext'
import type { ForestOwnerModel } from '@/types/ForestOwners/ForestOwner'

export const Route = createFileRoute('/framework/forestowner/$forestownerId')({
  component: ForestOwnerSandboxLayout,
  staticData: {
    breadcrumb: 'Framework · Skogeieroversikt',
  },
})

/**
 * Sandbox root layout for the ForestOwner Framework proof-of-concept: wires the generic
 * `EntityProfileLayout` to the *existing* production `getForestOwnerQuery`/
 * `getCooperativeMembershipQuery` and `ForestOwnerTypeBadge` — no new endpoints, no changes
 * to `useDocuments`/`getForestOwner`/etc. Only the Overview and Documents tabs render real
 * content (see sibling `index.tsx`/`documents.tsx`); all other tabs are placeholders.
 */
function ForestOwnerSandboxLayout() {
  const { forestownerId } = Route.useParams()
  const { currentTenant } = useTenant()
  const tabs = useForestOwnerSandboxTabs(forestownerId)
  const { Provider } = forestOwnerSandboxProfileContext

  const { data, isLoading, isError, refetch } = useQuery(
    getForestOwnerQuery(forestownerId, currentTenant.id),
  )

  return (
    <EntityProfileLayout<ForestOwnerModel>
      Provider={Provider}
      useEntityQuery={() => ({ data, isLoading, isError, refetch })}
      getHeaderProps={(entity): EntityProfileHeaderProps => ({
        title: formatFullName(entity.name),
        identifiers: [
          { type: 'customerNumber', value: entity.customerNumber },
          {
            type: 'organizationNumber',
            value: entity.operatingOrganization?.organizationNumber,
          },
        ],
        badges: <ForestOwnerBadgesSlot forestOwner={entity} forestownerId={forestownerId} />,
      })}
      tabs={tabs}
      ariaLabelKey="forestowner-search-overview.tabsAriaLabel"
    >
      <Outlet />
    </EntityProfileLayout>
  )
}

function ForestOwnerBadgesSlot({
  forestOwner,
  forestownerId,
}: {
  forestOwner: ForestOwnerModel
  forestownerId: string
}) {
  const { data: cooperativeMembership } = useQuery({
    ...getCooperativeMembershipQuery({ forestOwnerId: forestownerId }),
    enabled: !!forestownerId,
  })

  return (
    <ForestOwnerSandboxBadges
      forestOwner={forestOwner}
      cooperativeMembership={cooperativeMembership}
    />
  )
}
